<template>
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" />
    </template>
    <template #default>
      <Breadcrumbs
        :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Statistik Pengunjung', href: route('visitor.index') },
        ]"
      />
      <div class="container px-4 mx-auto mt-4">
        <div class="flex flex-col">
          <div class="pb-4 mb-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <h2 class="text-2xl font-semibold text-gray-800">
                Statistik Pengunjung Website
              </h2>
              <Link
                :href="route('visitor.export')"
                class="px-4 py-2 font-medium text-white transition-colors bg-green-600 rounded hover:bg-green-700"
              >
                <span class="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  Export CSV
                </span>
              </Link>
            </div>
          </div>

          <!-- Stats Heading -->
          <div class="mb-6">
            <h3 class="text-xl font-semibold text-gray-800">Total Kunjungan</h3>
            <p class="text-sm text-gray-500">
              Menampilkan semua kunjungan ke situs
            </p>
          </div>

          <!-- Total Visits Stats Grid -->
          <div
            class="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 md:grid-cols-4"
          >
            <!-- Total Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Total Pengunjung</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ totalVisits }}
                  </p>
                </div>
                <div class="p-3 text-blue-600 bg-blue-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Today Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Hari Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ todayVisits }}
                  </p>
                </div>
                <div class="p-3 text-green-600 bg-green-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Monthly Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Bulan Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ monthlyVisits }}
                  </p>
                </div>
                <div class="p-3 text-purple-600 bg-purple-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Yearly Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Tahun Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ yearlyVisits }}
                  </p>
                </div>
                <div class="p-3 text-indigo-600 bg-indigo-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Unique Visitors Heading -->
          <div class="mt-10 mb-6">
            <h3 class="text-xl font-semibold text-gray-800">Pengunjung Unik</h3>
            <p class="text-sm text-gray-500">
              Berdasarkan alamat IP yang berbeda
            </p>
          </div>

          <!-- Unique Visitors Stats Grid -->
          <div
            class="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 md:grid-cols-4"
          >
            <!-- Total Unique Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Total Pengunjung Unik</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ totalUniqueVisits || 0 }}
                  </p>
                </div>
                <div class="p-3 text-blue-600 bg-blue-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Today Unique Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Unik Hari Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ todayUniqueVisits || 0 }}
                  </p>
                </div>
                <div class="p-3 text-green-600 bg-green-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Monthly Unique Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Unik Bulan Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ monthlyUniqueVisits || 0 }}
                  </p>
                </div>
                <div class="p-3 text-purple-600 bg-purple-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <!-- Yearly Unique Visits -->
            <div class="p-4 bg-white rounded-lg shadow-sm sm:p-6">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500">Pengunjung Unik Tahun Ini</p>
                  <p class="text-2xl font-semibold text-gray-900">
                    {{ yearlyUniqueVisits || 0 }}
                  </p>
                </div>
                <div class="p-3 text-indigo-600 bg-indigo-100 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Chart Section -->
          <div class="p-4 mb-8 bg-white rounded-lg shadow-sm sm:p-6">
            <h3 class="mb-4 text-lg font-semibold text-gray-800">
              Pengunjung 30 Hari Terakhir
            </h3>
            <div class="h-64">
              <!-- Replace this with your chart implementation -->
              <div class="flex items-center h-full">
                <div
                  v-for="(item, index) in dailyVisits"
                  :key="index"
                  class="flex flex-col items-center mx-1"
                >
                  <div
                    class="w-4 bg-blue-500 rounded-t-sm"
                    :style="{
                      height: `${(item.count / maxVisitCount) * 180}px`,
                    }"
                    :title="`${item.date}: ${item.count} pengunjung`"
                  ></div>
                  <span
                    class="mt-2 text-xs text-gray-500"
                    v-if="index % 5 === 0"
                    >{{ item.date }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { computed, inject } from "vue";
import { Link } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
const route = inject('route');

const props = defineProps({
  totalVisits: Number,
  todayVisits: Number,
  monthlyVisits: Number,
  yearlyVisits: Number,
  dailyVisits: Array,
  totalUniqueVisits: Number,
  todayUniqueVisits: Number,
  monthlyUniqueVisits: Number,
  yearlyUniqueVisits: Number,
  dailyUniqueVisits: Array,
});

// Calculate maximum number of visits for chart scaling
const maxVisitCount = computed(() => {
  if (!props.dailyVisits || props.dailyVisits.length === 0) return 1;
  return Math.max(...props.dailyVisits.map((day) => day.count)) || 1;
});
</script>
